import { toNumber } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Snackbar } from 'src/components/snackbar';
import {
  SortMenuOption,
  SortOptions,
} from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { Column } from 'src/components/table/CustomTable.interface';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants';
import { PATHS } from 'src/constants/paths';
import {
  useGetPaginatedLocations,
  useParamQuery,
  useUpdateLocationsConfig,
  useViewChannels,
} from 'src/hooks';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { ILocation, LayoutTypes } from 'src/interfaces';
import { formatQuery, lang } from 'src/utils';

import {
  applyToggleToConfig,
  buildToggleFromConfig,
} from './BulkEditMessagingPage.helper';
import { MessagingToggle } from './BulkEditMessagingPage.interface';

export const useBulkEditMessaging = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: channels = [] } = useViewChannels();

  const { onChangeParams, getParamsValue } = useParamQuery({
    sort: ['name'],
    page: ['1'],
    pageSize: ['20'],
  });

  const pageParam = toNumber(getParamsValue('page')?.[0] || 1);
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const pageSizeFromUrl = getParamsValue('pageSize')?.[0];
  const rowsPerPageFromUrl = getParamsValue('rows_per_page')?.[0];
  const rowsPerPageParam =
    toNumber(pageSizeFromUrl || rowsPerPageFromUrl) || 20;

  const apiFilters = useMemo(() => {
    const filtersFromUrl = formatQuery(location.search);
    const filtersForApi = filtersFromUrl.filter(
      (f) => !f.startsWith('pageSize=') && !f.startsWith('page='),
    );

    const pageSizeForQueryKey = pageSizeFromUrl || rowsPerPageFromUrl || '20';

    return [...filtersForApi, 'pageSize=0', `_cacheKey=${pageSizeForQueryKey}`];
  }, [location.search, pageSizeFromUrl, rowsPerPageFromUrl]);

  const { data: locationTabData, isLoading: isLocationTabDataLoading } =
    useGetPaginatedLocations(apiFilters, false);
  const locations = useMemo(
    () => locationTabData?.locations ?? [],
    [locationTabData],
  );

  const setPage = useCallback(
    (newPage: number) => {
      onChangeParams('page', [`${newPage}`]);
    },
    [onChangeParams],
  );

  const setRowsPerPage = useCallback(
    (newRowsPerPage: number) => {
      onChangeParams('pageSize', [`${newRowsPerPage}`]);
      onChangeParams('page', ['1']);
    },
    [onChangeParams],
  );

  const sortFromUrl = getParamsValue('sort')?.[0] || 'name';
  const sortOption: SortMenuOption = useMemo(
    () => ({
      option:
        (sortFromUrl.replace('-', '') as SortOptions) ||
        SortOptions.ALPHABETICALLY,
      order: (sortFromUrl.includes('-') ? 'desc' : 'asc') as 'desc' | 'asc',
    }),
    [sortFromUrl],
  );

  const setSortOption = useCallback(
    (newSortOption: SortMenuOption) => {
      const sortValue = `${newSortOption.order === 'desc' ? '-' : ''}${
        newSortOption.option
      }`;
      onChangeParams('sort', [sortValue]);
    },
    [onChangeParams],
  );

  const handleBack = () => navigate(PATHS.OO_LOCATION_PAGE);

  const [isSaving, setIsSaving] = useState(false);

  const locationsContainOutdoorType = useMemo(() => {
    return locations.filter((location) =>
      location.channels?.some(
        (locationChannel: unknown) =>
          channels.find(
            (channel) => channel.channel_name === (locationChannel as string),
          )?.layoutType?.name === LayoutTypes.OUTDOOR,
      ),
    );
  }, [channels, locations]);

  const initialState = useMemo(() => {
    return locationsContainOutdoorType.reduce<Record<string, MessagingToggle>>(
      (acc, location) => {
        acc[location.location_code] = buildToggleFromConfig(location.config);

        return acc;
      },
      {},
    );
  }, [locationsContainOutdoorType]);

  const [toggles, setToggles] =
    useState<Record<string, MessagingToggle>>(initialState);
  const editedLocationCodesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setToggles((prevToggles) => {
      const nextToggles = { ...initialState };

      editedLocationCodesRef.current.forEach((locationCode) => {
        if (prevToggles[locationCode]) {
          nextToggles[locationCode] = prevToggles[locationCode];
        }
      });

      return nextToggles;
    });
  }, [initialState]);

  const summary = useMemo(() => {
    return locationsContainOutdoorType.reduce(
      (acc, location) => {
        const toggle =
          toggles[location.location_code] ??
          initialState[location.location_code] ??
          buildToggleFromConfig(location.config);
        if (toggle?.banner) acc.banner += 1;
        if (toggle?.messaging) acc.messaging += 1;

        return acc;
      },
      { banner: 0, messaging: 0 },
    );
  }, [initialState, locationsContainOutdoorType, toggles]);

  const sortedLocations = useMemo(() => {
    const sorted = [...locations].sort((a, b) => {
      const nameA = (a.name || '').toLocaleLowerCase();
      const nameB = (b.name || '').toLocaleLowerCase();

      if (nameA < nameB) {
        return sortOption.order === 'asc' ? -1 : 1;
      }
      if (nameA > nameB) {
        return sortOption.order === 'asc' ? 1 : -1;
      }

      return 0;
    });

    return sorted;
  }, [locations, sortOption.order]);

  const rowsPerPage = rowsPerPageParam;
  const clientSideTotalPages = Math.max(
    1,
    Math.ceil(sortedLocations.length / rowsPerPage),
  );
  const clientSidePaginatedLocations = sortedLocations.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  const sortItems = useMemo(
    () => [
      {
        label: lang.aToZ,
        isSelected:
          sortOption.option === SortOptions.ALPHABETICALLY &&
          sortOption.order === 'asc',
        onClick: () =>
          setSortOption({
            option: SortOptions.ALPHABETICALLY,
            order: 'asc',
          }),
      },
      {
        label: lang.zToA,
        isSelected:
          sortOption.option === SortOptions.ALPHABETICALLY &&
          sortOption.order === 'desc',
        onClick: () =>
          setSortOption({
            option: SortOptions.ALPHABETICALLY,
            order: 'desc',
          }),
      },
    ],
    [sortOption, setSortOption],
  );

  const rowsPerPageItems = useMemo(() => {
    const standardOptions = [20, 50, 100];
    const isStandardValue = standardOptions.includes(rowsPerPage);
    const isShowingAll = rowsPerPage >= sortedLocations.length;

    if (isStandardValue) {
      return undefined;
    }

    const items = [
      {
        label: '20',
        isSelected: false,
        onClick: () => setRowsPerPage(20),
      },
      {
        label: '50',
        isSelected: false,
        onClick: () => setRowsPerPage(50),
      },
      {
        label: '100',
        isSelected: false,
        onClick: () => setRowsPerPage(100),
      },
    ];

    // Only add custom number option if it's less than total count
    if (!isShowingAll) {
      items.push({
        label: String(rowsPerPage),
        isSelected: true,
        onClick: () => setRowsPerPage(rowsPerPage ?? 20),
      });
    }

    // Add "All" option - mark as selected if showing all items
    items.push({
      label: 'All',
      isSelected: isShowingAll,
      onClick: () => setRowsPerPage(sortedLocations.length),
    });

    return items;
  }, [rowsPerPage, sortedLocations.length, setRowsPerPage]);

  const columns: Column[] = [
    { id: 'name', label: lang.locationName, width: '40%' },
    { id: 'store_number', label: lang.storeNumber, width: '18%' },
    { id: 'tags', label: lang.tags, width: '22%' },
    { id: 'banner', label: lang.banner, width: '10%', align: 'center' },
    { id: 'messaging', label: lang.messaging, width: '10%', align: 'center' },
  ];

  const updateLocationsConfig = useUpdateLocationsConfig();

  const handleToggleChange = (
    locationCode: string,
    key: keyof MessagingToggle,
    value: boolean,
  ) => {
    editedLocationCodesRef.current.add(locationCode);
    setToggles((prev) => {
      const location = locationsContainOutdoorType.find(
        (loc) => loc.location_code === locationCode,
      );
      const fallback = location
        ? buildToggleFromConfig(location.config)
        : { banner: true, messaging: true };

      return {
        ...prev,
        [locationCode]: {
          ...(prev[locationCode] ?? initialState[locationCode] ?? fallback),
          [key]: value,
        },
      };
    });
  };

  const handleToggleAll = (key: keyof MessagingToggle, value: boolean) => {
    setToggles((prev) => {
      const updated = { ...prev };

      locationsContainOutdoorType.forEach((location) => {
        editedLocationCodesRef.current.add(location.location_code);
        const existing = updated[location.location_code] ?? {
          ...(initialState[location.location_code] ??
            buildToggleFromConfig(location.config)),
        };

        updated[location.location_code] = {
          ...existing,
          [key]: value,
        };
      });

      return updated;
    });
  };

  const handleSave = async () => {
    const persistUpdates = async () => {
      try {
        setIsSaving(true);

        const payload = locationsContainOutdoorType.map((location) => {
          const currentToggle =
            toggles[location.location_code] ??
            initialState[location.location_code];

          if (!currentToggle) {
            return {
              ...location,
              config: location.config,
            } as unknown as ILocation;
          }

          const updatedConfig = applyToggleToConfig(
            location.config,
            currentToggle,
          );

          return {
            ...location,
            config: updatedConfig,
            narrative: narrativeGenerators[COLLECTIONS.LOCATIONS][
              USER_ACTIONS.UPDATE
            ](
              currentToggle.banner,
              currentToggle.messaging,
              !!locationDirtyFlags.get(location.location_code)?.banner,
              !!locationDirtyFlags.get(location.location_code)?.messaging,
              location.location_code,
            ),
          } as unknown as ILocation;
        });

        if (payload.length === 0) {
          handleBack();

          return;
        }

        Snackbar.info(lang.YourOverridesAreBeingSaved);
        await updateLocationsConfig.mutateAsync(payload);
        editedLocationCodesRef.current.clear();
        Snackbar.success(lang.OverridesSavedSuccessfully);
      } catch (error) {
        Snackbar.error(lang.failedSaveText);
      } finally {
        setIsSaving(false);
      }
    };

    if (!isSaving) {
      await persistUpdates();
    }
  };

  const locationDirtyFlags = useMemo(
    () =>
      new Map(
        locations.map((location) => {
          const initialToggle =
            initialState[location.location_code] ??
            buildToggleFromConfig(location.config);
          const currentToggle =
            toggles[location.location_code] ?? initialToggle;

          return [
            location.location_code,
            {
              banner: initialToggle.banner !== currentToggle.banner,
              messaging: initialToggle.messaging !== currentToggle.messaging,
            },
          ];
        }),
      ),
    [initialState, locations, toggles],
  );

  const isDirty = useMemo(() => {
    return locations.some((location) => {
      const initialToggle =
        initialState[location.location_code] ??
        buildToggleFromConfig(location.config);
      const currentToggle = toggles[location.location_code] ?? initialToggle;

      return (
        initialToggle.banner !== currentToggle.banner ||
        initialToggle.messaging !== currentToggle.messaging
      );
    });
  }, [initialState, locations, toggles]);

  const bannerHeaderState = useMemo(() => {
    const total = locations.length;
    if (total === 0) {
      return { allChecked: false, indeterminate: false };
    }

    const checkedCount = locations.reduce((count, location) => {
      const value =
        toggles[location.location_code] ??
        initialState[location.location_code] ??
        buildToggleFromConfig(location.config);

      return value.banner ? count + 1 : count;
    }, 0);

    return {
      allChecked: checkedCount === total,
      indeterminate: checkedCount > 0 && checkedCount < total,
    };
  }, [initialState, locations, toggles]);

  const messagingHeaderState = useMemo(() => {
    const total = locations.length;
    if (total === 0) {
      return { allChecked: false, indeterminate: false };
    }

    const checkedCount = locations.reduce((count, location) => {
      const value =
        toggles[location.location_code] ??
        initialState[location.location_code] ??
        buildToggleFromConfig(location.config);
      const isChecked = value.messaging ?? true;

      return isChecked ? count + 1 : count;
    }, 0);

    return {
      allChecked: checkedCount === total,
      indeterminate: checkedCount > 0 && checkedCount < total,
    };
  }, [initialState, locations, toggles]);

  return {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    clientSideTotalPages,
    clientSidePaginatedLocations,
    rowsPerPageItems,
    sortOption,
    setSortOption,
    sortItems,
    isLocationTabDataLoading,
    sortedLocations,
    isSaving,
    toggles,
    initialState,
    summary,
    isDirty,
    bannerHeaderState,
    messagingHeaderState,
    columns,
    locationsContainOutdoorType,
    handleToggleChange,
    handleToggleAll,
    handleSave,
    handleBack,
  };
};
